import type { WithNegated } from '~/types/util/string'
import type { CollectionRequestPaginationParams, CollectionResponse } from '~/types/api/v2'

import type { WorkspaceIdentifier } from '../types'

import { useFetchWithDefaults } from '../useFetchWithDefaults'

export const responseDataTypeName: Contact['_type'] = 'contact'

type SortCriterion = 'updated_at'

// TODO DEV-6021 the model types are not generated, so we have to define them here
export interface ContactPerson {
  id: string
  _type: 'contact_person'
  first_name: string
  last_name: string
  title: string | null
  email: string | null
  phone: string | null
  street: string | null
  city: string | null
  post_code: string | null
  country: string | null
  notes: string | null
  companies?: Array<Omit<ContactCompany, 'persons'>>
  is_system: boolean
}

export interface ContactCompany {
  id: string
  _type: 'contact_company'
  name: string
  ident: string | null
  street: string | null
  city: string | null
  post_code: string | null
  country: string | null
  notes: string | null
  persons?: Array<Omit<ContactPerson, 'companies'>>
  is_system: boolean
}

export type PayloadHelper<T extends ContactCompany | ContactPerson> = Omit<T, 'id' | '_type' | 'is_system'>

export type CompanyMutationPayload = PayloadHelper<Omit<ContactCompany, 'persons'>>
  & { persons?: Array<{ id: string }> }

export type CompanyUpdatePayload = Pick<CompanyMutationPayload, 'notes' | 'persons'> & Partial<Omit<CompanyMutationPayload, 'notes' | 'persons'>>

export type PersonMutationPayload = PayloadHelper<Omit<ContactPerson, 'companies'>>
  & { companies?: Array<{ id: string }> }

export type PersonUpdatePayload =
  Pick<PersonMutationPayload, 'title' | 'notes' | 'companies'>
  & Partial<Omit<PersonMutationPayload, 'title' | 'notes' | 'companies'>>

export interface Contact {
  id: string
  _type: 'contact'
  updated_at: string
  person: ContactPerson
  company?: ContactCompany
}

export type ParametersGetAll = {
  sort?: WithNegated<SortCriterion>[]
  'filter[query]'?: string
} & CollectionRequestPaginationParams

export type ParametersGetEntity<TInclude extends 'persons' | 'companies'> = {
  include?: TInclude
  'filter[query]'?: string
} & CollectionRequestPaginationParams

export const useFetchContact = () => {
  const url = '/v2/contacts'
  const companies = '/v2/contact-companies'
  const persons = '/v2/contact-persons'

  const { get, post, patch, deleteRequest } = useFetchWithDefaults()

  const getAll = ({
    params,
    workspaceId,
    signal,
  }:
    WorkspaceIdentifier & {
      params?: ParametersGetAll
      signal?: AbortSignal
    }) => get<CollectionResponse<Contact>>({
    url,
    workspaceId,
    fetchOptions: {
      params,
      signal,
    },
  })

  const getCompanies = (
    { workspaceId, params, signal }:
      WorkspaceIdentifier & {
        params?: ParametersGetEntity<'persons'>
        signal?: AbortSignal
      }) => get<CollectionResponse<ContactCompany>>({
    url: companies,
    workspaceId,
    fetchOptions: {
      params,
      signal,
    },
  })

  const getPersons = ({ workspaceId, params, signal }: WorkspaceIdentifier & {
    params?: ParametersGetEntity<'companies'>
    signal?: AbortSignal
  }) => get<CollectionResponse<ContactPerson>>({
    url: persons,
    workspaceId,
    fetchOptions: {
      params,
      signal,
    },
  })

  const createCompany = ({
    workspaceId,
    payload,
  }:
    WorkspaceIdentifier & {
      payload: CompanyMutationPayload
    }) =>
    post<{ data: ContactCompany }>({
      url: companies,
      workspaceId,
      fetchOptions: {
        body: payload,
      },
    })

  const updateCompany = ({
    workspaceId,
    payload,
    id,
  }: WorkspaceIdentifier & {
    payload: CompanyUpdatePayload
    id: string
  }) => patch<{ data: ContactCompany }>({
    url: `${companies}/${id}`,
    workspaceId,
    fetchOptions: {
      body: payload,
    },
  })

  const deleteCompany = ({
    workspaceId,
    id,
  }: WorkspaceIdentifier & {
    id: string
  }) => deleteRequest<{ data: ContactCompany }>({
    url: `${companies}/${id}`,
    workspaceId,
  })

  const createPerson = ({
    workspaceId,
    payload,
  }:
    WorkspaceIdentifier & {
      payload: PersonMutationPayload
    }) =>
    post<{ data: ContactPerson }>({
      url: persons,
      workspaceId,
      fetchOptions: {
        body: payload,
      },
    })

  const updatePerson = ({ workspaceId, id, payload }:
    WorkspaceIdentifier & {
      id: string
      payload: PersonUpdatePayload
    }) =>
    patch<{ data: ContactPerson }>({
      url: `${persons}/${id}`,
      workspaceId,
      fetchOptions: {
        body: payload,
      },
    })

  const deletePerson = ({ workspaceId, id }: WorkspaceIdentifier & { id: string }) =>
    deleteRequest({
      url: `${persons}/${id}`,
      workspaceId,
    })

  const getByUserId = ({ workspaceId, userId }: WorkspaceIdentifier & { userId: string }) =>
    get<{ data: ContactPerson }>({
      url: `${persons}/by-user-id/${userId}`,
      workspaceId,
    })

  return {
    getAll,
    getCompanies,
    getByUserId,
    createCompany,
    updateCompany,
    deleteCompany,
    createPerson,
    updatePerson,
    deletePerson,
    getPersons,
  }
}
